import React from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  ArrowLeft,
  ShieldAlert,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { routeConstants } from '../constants/routeConstants';

const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600">
          <ShieldAlert className="h-8 w-8" />
        </div>

        <h1 className="mt-5 text-2xl font-bold text-gray-900">
          Access denied
        </h1>

        <p className="mt-2 text-sm leading-6 text-gray-500">
          Your current role does not have permission to access
          this page. Contact the school administrator when you
          require access.
        </p>

        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Go back
          </button>

          <button
            type="button"
            onClick={() =>
              navigate(routeConstants.DASHBOARD, {
                replace: true,
              })
            }
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />

      <p className="mt-3 text-sm font-medium text-gray-500">
        Loading your account...
      </p>
    </div>
  </div>
);

const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredPermissions,
  permissionMode = 'all',
}) => {
  const {
    user,
    activeRole,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuth();

  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return (
      <Navigate
        to={routeConstants.LOGIN}
        state={{
          from: location,
        }}
        replace
      />
    );
  }

  const isRoleSelectionPage =
    location.pathname ===
    routeConstants.ROLE_SELECTION;

  if (!activeRole && !isRoleSelectionPage) {
    return (
      <Navigate
        to={routeConstants.ROLE_SELECTION}
        state={{
          from: location,
        }}
        replace
      />
    );
  }

  if (requiredPermission) {
    const allowed = hasPermission(
      requiredPermission,
    );

    if (!allowed) {
      return <AccessDenied />;
    }
  }

  if (
    Array.isArray(requiredPermissions) &&
    requiredPermissions.length > 0
  ) {
    const allowed =
      permissionMode === 'any'
        ? hasAnyPermission(requiredPermissions)
        : hasAllPermissions(requiredPermissions);

    if (!allowed) {
      return <AccessDenied />;
    }
  }

  return children;
};

export default ProtectedRoute;